import { Link } from 'react-router-dom';
import { CustomCodesPanel } from '@tinct/web-feature-organizations';
import { useGetCustomCodes } from '@tinct/core-api';
import {
  useCurrentWorkspaceUrlPrefix,
  useGetCurrentWorkspaceId,
} from '@tinct/core-workspace';
import { pluralize } from '@tinct/core-utils';

export type PageCustomCodeSectionProps = {
  landingPageId: string;
};

/**
 * Page-scoped custom code for the campaign's hosted landing page. Entries
 * here layer AFTER the workspace-wide ones; the summary line links to those.
 */
export function PageCustomCodeSection({ landingPageId }: PageCustomCodeSectionProps) {
  const workspaceId = useGetCurrentWorkspaceId();
  const workspaceUrlPrefix = useCurrentWorkspaceUrlPrefix();

  const { data: workspaceCodes } = useGetCustomCodes(
    { organizationId: workspaceId ?? '' },
    { enabled: !!workspaceId },
  );
  const workspaceCount = (workspaceCodes ?? []).filter(
    (code) => code.enabled !== false,
  ).length;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <h2 className="font-heading text-xl font-semibold tracking-tight">
          Custom code
        </h2>
        <p className="text-sm text-muted-foreground">
          Scripts, styles and structured data loaded on this landing page only.
        </p>
      </div>

      <CustomCodesPanel
        scope={{ landingPageId }}
        summary={
          workspaceCount > 0 ? (
            <p className="text-sm text-muted-foreground">
              {workspaceCount} workspace-wide{' '}
              {pluralize(workspaceCount, 'script')} also{' '}
              {pluralize(workspaceCount, 'loads', 'load')} on this page —{' '}
              <Link
                to={`${workspaceUrlPrefix}/settings/domain-hosting`}
                className="underline underline-offset-2 hover:text-foreground"
              >
                manage them in the workspace settings
              </Link>
              .
            </p>
          ) : undefined
        }
      />
    </div>
  );
}
